import { Box, Button, Flex, Select, Text, VStack } from '@chakra-ui/react'
import { Mic, MicAIOn } from '@components/icons'
import { useAudioCapture, useWidget } from '@hooks'
import { useStoreState } from 'easy-peasy'
import { times } from 'lodash'
import React, { useEffect } from 'react'
import type { StoreModel } from 'src/store/types'
import type { StepProps } from './types'

export const AudioLevel = () => {
  const { audioLevel } = useStoreState((state: StoreModel) => state.volume)
  const { isMuted } = useAudioCapture()
  const { settings } = useWidget()

  const numBars = 15

  return (
    <Flex justifyContent="space-between" width="100%">
      {times(numBars, i => (
        <Box
          key={i}
          h="16px"
          w="6px"
          borderRadius="4px"
          bg={
            audioLevel >= (i + 1) * (1 / numBars) && !isMuted
              ? settings.minifiedViewSettings?.audioIndicatorColor || '#68E19F'
              : '#E4E5E6'
          }
        />
      ))}
    </Flex>
  )
}

export const MicrophoneSelector = () => {
  const { audioInputs, selectedAudioInput, setSelectedAudioInput } =
    useAudioCapture()

  const { settings } = useWidget()

  const handleMicChange = (event: any) => {
    setSelectedAudioInput(event.target.value)
  }

  return (
    <VStack spacing={0} textAlign="left" w="100%">
      <Box w="100%">
        <Select
          onChange={handleMicChange}
          value={selectedAudioInput}
          borderColor="gray.300"
          borderRadius={
            settings.minifiedViewSettings?.inputs?.borderRadius ?? '5px'
          }
          _active={{ borderColor: 'gray.300' }}
          _focus={{ borderColor: 'gray.300' }}
          _hover={{ borderColor: 'gray.300' }}
        >
          {audioInputs.map(input => (
            <option key={input.deviceId} value={input.deviceId}>
              {input.label}
            </option>
          ))}
        </Select>
      </Box>
      <Box w="100%">
        <Flex
          justifyContent="space-between"
          alignItems="center"
          h="40px"
          pr="xsmall"
        >
          <Box mr="xsmall">
            <Mic />
          </Box>
          <AudioLevel />
        </Flex>
      </Box>
    </VStack>
  )
}

export const SelectMic: React.FC<StepProps> = ({ nextStep }) => {
  const {
    testAudioInputs,
    hasResolvedInputDevicePermissions,
    promptForDevicePermissions,
    permissionStatus
  } = useAudioCapture()

  const { settings } = useWidget()

  useEffect(() => {
    promptForDevicePermissions()
    testAudioInputs()
  }, [hasResolvedInputDevicePermissions])

  if (permissionStatus === 'granted') {
    return (
      <Flex height="100%" flexDirection="column">
        <Text fontSize="16px">Microphone</Text>
        <Text fontSize="10px" mb="xsmall">
          Choose and test your audio input
        </Text>
        <MicrophoneSelector />
        <Button
          mt="auto"
          backgroundColor={
            settings.minifiedViewSettings?.buttons?.primaryBackgroundColor ??
            'primary'
          }
          color={
            settings.minifiedViewSettings?.buttons?.primaryTextColor ?? 'white'
          }
          borderRadius={
            settings.minifiedViewSettings?.buttons?.borderRadius ?? '24px'
          }
          borderColor={
            settings.minifiedViewSettings?.buttons?.primaryBorderColor ??
            'primary'
          }
          borderWidth={
            settings.minifiedViewSettings?.buttons?.primaryBorderWidth ?? '1px'
          }
          onClick={nextStep}
        >
          Next
        </Button>
      </Flex>
    )
  } else {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        height="100%"
      >
        <MicAIOn fill="black" width="40px" height="40px" />
        <Text textAlign="center">
          Allow your browser to use your microphone
        </Text>
      </Flex>
    )
  }
}
